(ns arthur.domain.cut
  "The eraser's cut: a shape's ring with a stroke taken out of it.

  Illustrator's and Flash's eraser, not a raster one: what is left is the
  SHAPE, with the cut edge made of its own points — points the pen moves, keys
  and tweens like any others. A cut through the middle leaves two pieces, and
  a cut inside it leaves a hole, which is bridged into the one ring as a brush
  stroke's is (see `outline`).

  In stage pixels, on both sides: the preview cuts the rings it is about to
  draw, and the saved cut cuts the same rings and takes the answer back into
  the shape's own coordinates — one function, so the preview is the result."
  (:require ["polygon-clipping" :as clipping]
            [arthur.domain.channel :as channel]
            [arthur.domain.nest :as nest]
            [arthur.domain.node :as node]
            [arthur.domain.outline :as outline]
            [arthur.domain.paint :as paint]))

(defn- area [ring]
  (let [ps (vec (partition 2 ring)) n (count ps)]
    (js/Math.abs (/ (reduce + (map (fn [i] (let [[ax ay] (ps i) [bx by] (ps (mod (inc i) n))]
                                             (- (* ax by) (* bx ay))))
                                   (range n)))
                    2))))

(defn cut
  "Ring `ring` with `cutters` — each `[outer & holes]` — taken out of it: one
  ring per piece left, biggest first, an empty vector when nothing is left, or
  nil when the cut does not touch it."
  [ring cutters]
  (let [shape  #js [(outline/->js ring)]
        knife  (into-array (map #(into-array (map outline/->js %)) cutters))]
    (when (seq (array-seq (clipping/intersection shape knife)))
      (->> (array-seq (clipping/difference shape knife))
           (map (fn [^js poly] (mapv outline/->ring (array-seq poly))))
           (sort-by (comp - area first))
           (mapv outline/join)))))

(defn- through [m pts]
  (let [out (js/Float64Array. 2)]
    (into [] (mapcat (fn [[x y]] (node/apply-pt! out 0 m x y) [(aget out 0) (aget out 1)]))
          (partition 2 pts))))

(defn erase
  "`clip` with `cutters`, in the stage pixels of symbol `open` at frame `f`,
  cut out of each shape at row path in `paths`, on the frame each is showing.

  The shape keeps the biggest piece, on a key at that frame — made there if
  the frame had none, so the keys either side keep their points. Every other
  piece is a new shape of the same colour, its id the next of `ids`. A shape
  cut away entirely is deleted.

  TWO SPACES COME BACK OUT, and they are not the same one. The piece the shape
  KEEPS is written into that shape's own geometry, so it comes back through
  `world⁻¹`, the node's own coordinates. Every other piece becomes a NEW node
  beside it, whose points are read against its own fresh transform, so those come
  back through `parent⁻¹` — the space a node's `pos` lives in, which is what
  `paint/new-shape` takes and centres.

  Both were `world⁻¹` before, and that was wrong for the new pieces by exactly
  the cut shape's own transform. It could not be seen while every drawing had an
  identity transform, which was true of all of them for as long as a stroke was
  stored exactly as drawn: the two spaces coincided, so erasing a shape nobody had
  moved worked, and erasing one somebody had moved scattered the offcuts."
  [clip store open f paths cutters ids]
  (first
   (reduce
    (fn [[clip ids] path]
      (let [{:keys [sid id frame world parent]} (nest/placement clip store open path f)
            n    (get-in clip [:symbols sid :nodes id])
            geom (get-in n [:channels paint/geometry])
            inv  (when world (node/invert world))
            up   (when parent (node/invert parent))
            left (when (and inv up geom)
                   (cut (through world (channel/value-at geom frame store)) cutters))]
        (cond
          (nil? left)   [clip ids]
          (empty? left) [(nest/delete-node clip sid id) ids]
          :else
          (let [[keep & more] left
                colour (channel/value-at (get-in n [:channels [:style :color]]) frame store)
                kept   (-> (if (contains? (:keys geom) frame) clip (paint/add-key clip sid id frame))
                           (paint/set-points sid id frame (through inv keep)))]
            [(reduce (fn [c [nid pts]] (paint/new-shape c sid nid frame (through up pts) colour))
                     kept (map vector ids more))
             (drop (count more) ids)]))))
    [clip ids] paths)))
